import { usePadsStore } from "@/stores/usePadsStore"
import { VPadStage, VPadState } from "@/interfaces/virtualPad_types"
import { VPad } from "./VPad"

export class VPadManager {
  private static instance: VPadManager | undefined
  private readonly listVPads = new Map<string, VPad>()
  private onVPadRemoved?: (connectionId: string) => void

  /**
   * Initializes the VPadManager
   */
  private constructor () {
  }

  /**
   * Returns the VPadManager instance, creating it if necessary
   * @returns The VPadManager instance
   */
  public static getInstance (): VPadManager {
    if (VPadManager.instance === undefined) {
      VPadManager.instance = new VPadManager()
    }
    return VPadManager.instance
  }

  /**
   * Creates and connects a virtual pad using an index previously assigned by PadManager
   * This method must be called through PadManager.addVPad()
   * 
   * @param sessionId Session identifier
   * @param connectionId Connection identifier of the virtual pad
   * @param index Index assigned to the virtual pad
   * @throws If the virtual pad already exists, is being created or cannot connect
   */
  public async createAndConnect (sessionId: string, connectionId: string, index: number): Promise<void>  {
    if (this.listVPads.has(connectionId))
      throw new Error(`VPADMANAGER :: ADD :: A virtual pad with connectionID "${connectionId}" already exists`)

    const newVPad = VPad.create(
      sessionId,
      connectionId,
      index,
      {
        onRemoteClose: () => {
          console.info(`VPAD MANAGER :: Virtual pad ${connectionId} left voluntarily`)
          const removed = this.remove(connectionId)
          if (removed) this.onVPadRemoved?.(connectionId)
        },

        onDisconnected: () => {
          console.warn(`VPAD MANAGER :: Connection closed for virtual pad ${connectionId}`)
          const removed = this.remove(connectionId)
          if (removed) this.onVPadRemoved?.(connectionId)
        },
        
        onStageChanged: (stage) => {
          usePadsStore.getState().setVPadStage(
            connectionId,
            stage
          )

          if (typeof window !== 'undefined' && (window as any).soraStream && typeof (window as any).soraStream.vpadStageChanged  === 'function') {
            (window as any).soraStream.vpadStageChanged (connectionId, stage)
          }
        },

        onStateChanged: () => {          
          if (typeof window !== 'undefined' && (window as any).soraStream && typeof (window as any).soraStream.setVirtualGamepads === 'function') {
            (window as any).soraStream.setVirtualGamepads(this.getVPadStateList())
          }
        }
      }
    )

    /*
    * Register the VPad before starting the asynchronous connection.
    * This reserves its controller slot while it is connecting.
    */
    this.listVPads.set(connectionId, newVPad)

    usePadsStore.getState().addVPad({
      connectionId,
      stage: VPadStage.CONNECTING,
      index,
      connectedAt: null
    })
    
    try {
      await newVPad.connect()

      const connectedAt = newVPad.getConnectedAt()

      if (connectedAt === null)
        throw new Error(`VPADMANAGER :: ADD :: The virtual pad connected without a valid connectedAt timestamp`)

      usePadsStore.getState().setVPadStage(
        connectionId,
        VPadStage.CONNECTED
      )

      usePadsStore.getState().setVPadConnectedAt(
        connectionId,
        connectedAt
      )

      if (typeof window !== 'undefined' && (window as any).soraStream) {
        if(typeof (window as any).soraStream.vpadConnected === 'function') {
          (window as any).soraStream.vpadConnected(newVPad.getVPadState())
        }
      }
    } catch (error) {
      this.listVPads.delete(connectionId)
      usePadsStore.getState().removeVPad(connectionId)
      throw error
    }
  }

  /**
   * Notifies a virtual pad that it has been manually removed
   * and then removes its local connection.
   *
   * The local pad is removed even if the notification cannot be sent.
   *
   * @param connectionId Connection identifier of the virtual pad
   * @returns True if the virtual pad was removed, false if it didn't exist
   */
  public leave(connectionId: string): boolean {
    const vPad = this.listVPads.get(connectionId)
    if (vPad === undefined) return false

    try {
      vPad.requestLeave()
    } catch (error) {
      console.warn(
        `VPADMANAGER :: LEAVE :: Could not notify virtual pad "${connectionId}"`,
        error
      )
    } 
    
    return this.remove(connectionId)
  }

  /**
   * Closes and removes a virtual pad from the manager
   * 
   * The virtual pad is removed from the internal collection before closing
   * it because closing the DataChannel may trigger another removal callback
   * 
   * @param connectionId Connection identifier of the virtual pad
   * @returns True if the virtual pad was removed, false if it didn't exist
   */
  public remove (connectionId: string): boolean {
    const vPad = this.listVPads.get(connectionId)
    if (vPad === undefined) return false
    
    /*
    * Keep the last gamepad state before removing and closing it,
    * so the client can identify which virtual pad was disconnected.
    */
    const vPadState = vPad.getVPadState()
    
    this.listVPads.delete(connectionId)
    
    vPad.close()
    usePadsStore.getState().removeVPad(connectionId)

    if (
      typeof window !== 'undefined' &&
      (window as any).soraStream &&
      typeof (window as any).soraStream.vpadDisconnected === 'function'
    ) {
      (window as any).soraStream.vpadDisconnected(vPadState)
    }

    return true
  }

  /**
   * Applies a WebRTC answer to a virtual pad
   *
   * @param connectionId Connection identifier of the virtual pad
   * @param sdp Remote answer SDP
   */
  public async setAnswer(connectionId: string, sdp: string): Promise<void> {
    const vPad = this.listVPads.get(connectionId)

    if (vPad === undefined)
      throw new Error(`VPADMANAGER :: SET ANSWER :: The virtual pad with connectionID "${connectionId}" doesn't exist`)

    await vPad.setAnswer(sdp)
  }

  /**
   * Adds a remote ICE candidate to a virtual pad
   *
   * @param connectionId Connection identifier of the virtual pad
   * @param candidate Remote ICE candidate
   */
  public async addIceCandidate(connectionId: string, candidate: RTCIceCandidateInit): Promise<void> {
    const vPad = this.listVPads.get(connectionId)

    if (vPad === undefined)
      throw new Error(`VPADMANAGER :: ADDICECANDIDATE :: The virtual pad with connectionID "${connectionId}" doesn't exist`)

    await vPad.addIceCandidate(candidate)
  }

  /**
   * Checks whether a virtual pad exists
   * 
   * @param connectionId Connection identifier of the virtual pad
   * @returns True if the virtual pad exists
   */
  has(connectionId: string): boolean {
    return this.listVPads.has(connectionId)
  }

  /**
   * Returns all registered virtual pads.
   *
   * @returns The list of virtual pads currently managed
   */
  public getVPadList(): VPad[] {
    return Array.from(this.listVPads.values())
  }

  /**
   * Returns the current state of all registered virtual pads.
   *
   * @returns The current state of all virtual pads
   */
  public getVPadStateList(): VPadState[] {
    return this.getVPadList().map(
      (vPad) => vPad.getVPadState()
    )
  }

  /**
   * Returns the virtual pad assigned to a specific index.
   *
   * @param index Index assigned to the virtual pad
   * @returns The matching virtual pad or undefined
   */
  public getByIndex(index: number): VPad | undefined {
    return Array.from(this.listVPads.values()).find(
      (vPad) => vPad.getIndex() === index
    )
  }

  /**
   * Sets the callback invoked when a virtual pad is removed internally
   *
   * @param callback Callback invoked with the removed virtual pad connection identifier
   */
  public setOnVPadRemoved(callback: (connectionId: string) => void): void {
    this.onVPadRemoved = callback
  }

  /**
   * Changes the index assigned to a virtual pad.
   *
   * @param connectionId Connection identifier of the virtual pad
   * @param index New index assigned to the virtual pad
   */
  public setIndex(connectionId: string, index: number): void {
    const vPad = this.listVPads.get(connectionId)

    if (vPad === undefined)
      throw new Error(`VPADMANAGER :: SET INDEX :: The virtual pad with connectionID "${connectionId}" doesn't exist`)

    vPad.setIndex(index)
    usePadsStore.getState().setVPadIndex(connectionId, index)

    if (
      typeof window !== 'undefined' &&
      (window as any).soraStream &&
      typeof (window as any).soraStream.vpadIndexChanged === 'function'
    ) {
      (window as any).soraStream.vpadIndexChanged(vPad.getId(), index)
    }
  }

  /**
   * Removes a virtual pad to free a controller slot.
   *
   * The most recently connected virtual pad is removed first.
   * If no virtual pad has completed its connection, the most recently
   * created connecting virtual pad is removed.
   *
   * @returns The removed virtual pad connection identifier, or null
   */
  public removeToFreeSlot(): string | null {
    const vPads = Array.from(this.listVPads.values())

    const lastConnectedVPad = vPads
      .filter((vPad) => vPad.getConnectedAt() !== null)
      .sort((a, b) => {
        const aConnectedAt = a.getConnectedAt()
        const bConnectedAt = b.getConnectedAt()

        if (aConnectedAt === null || bConnectedAt === null) return 0

        return bConnectedAt - aConnectedAt
      })[0]

    /*
    * Map preserves insertion order. Reversing it gives us the most
    * recently created connecting virtual pad.
    */
    const lastConnectingVPad = [...vPads]
      .reverse()
      .find(
        (vPad) => vPad.getStage() === VPadStage.CONNECTING
      )

    const vPadToRemove = lastConnectedVPad ?? lastConnectingVPad

    if (vPadToRemove === undefined) return null

    const connectionId = vPadToRemove.getConnectionId()

    /*
    * Notify the remote pad when possible before closing and removing
    * its local connection.
    */
    try {
      vPadToRemove.requestEject()
    } catch (error) {
      console.warn(`VPADMANAGER :: REMOVE TO FREE SLOT :: Could not notify virtual pad "${connectionId}"`, error)
    } finally {
      this.remove(connectionId)
    }

    return connectionId
  }

  /**
   * Closes and removes all registered virtual pads
   * 
   * The manager and UI store are cleared before closing the connections 
   * because closing a DataChannel may trigger another removal callback
   */
  clear(): void {
    const vPads = Array.from(this.listVPads.values())

    this.listVPads.clear()
    usePadsStore.getState().clearVPads()

    vPads.forEach(vPad => {
      vPad.close()
    })
  }
}